import { FC, useContext, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import { useParams } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { createLoadOptions } from "../../../../../hooks/useDebounce";
import useI18n from "../../../../../hooks/useTranslations";
import { AdminPatrolSystem } from "../../../../../http/patrol/types/patrolSystems";
import { AdminRoofSubstructure } from "../../../../../http/patrol/types/roofSubstructures";
import {
  AdminSupportExtra,
  InsertSupportExtraBody,
  UpdateSupportExtraBody,
} from "../../../../../http/patrol/types/supportExtras";
import { usePatrolSystemsApi } from "../../../../../http/patrol/usePatrolSystems";
import { useRoofSubstructuresApi } from "../../../../../http/patrol/useRoofSubstructures";
import { useSupportExtrasApi } from "../../../../../http/patrol/useSupportExtras";
import { AdminItem, SearchAdminItem } from "../../../../../http/types/items";
import { useItemsApi } from "../../../../../http/useItems";
import Button from "../../../../ui/Button";
import Form from "../../../../ui/Form";
import FormField from "../../../../ui/FormField";
import FormFieldsContainer from "../../../../ui/FormFieldsContainer";
import Numeric from "../../../../ui/Numeric";
import { CustomAsyncSelect, CustomSelect } from "../../../../ui/select/CustomSelect";

type extraTypeValues = "only-life-line" | "only-anchor-point" | "both";

type extraType = {
  value: extraTypeValues;
  title: string;
};

interface SupportExtraFormBody {
  item: AdminItem | null;
  system: AdminPatrolSystem | null;
  roofSubstructure: AdminRoofSubstructure | null;
  seamHeight: number | null;
  minThickness: number | null;
  maxThickness: number | null;
  type: extraTypeValues;
  factor: number | null;
}

interface SupportExtraFormProps {
  defaultValues?: AdminSupportExtra;
  onClose: () => void;
}

const SupportExtraForm: FC<SupportExtraFormProps> = ({ defaultValues, onClose }: SupportExtraFormProps) => {
  const i18n = useI18n();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useContext(NotificationDispatch);
  const aborter = useRef(new AbortController());
  const { patrolSystemId } = useParams<{ patrolSystemId: string }>();
  const [defaultOptions, setDefaultOptions] = useState<SearchAdminItem[]>([]);

  const supportExtrasApi = useSupportExtrasApi();
  const patrolSystemsApi = usePatrolSystemsApi();
  const itemsApi = useItemsApi();
  const roofSubstructureApi = useRoofSubstructuresApi();

  const patrolSystemsResult = patrolSystemsApi.useAdminPatrolSystems({ limit: 100 }, aborter.current.signal);
  const roofSubstructureResult = roofSubstructureApi.useAdminRoofSubstructures({ limit: 100 }, aborter.current.signal);

  const extraTypes: extraType[] = [
    {
      value: "only-life-line",
      title: i18n.translation.common.onlyLifeLine,
    },
    {
      value: "only-anchor-point",
      title: i18n.translation.common.onlyAnchorPoint,
    },
    {
      value: "both",
      title: i18n.translation.common.lifeLinePlusAnchorPoint,
    },
  ];

  const type: extraTypeValues =
    defaultValues?.forAnchorPoints && defaultValues.forLifeLines
      ? "both"
      : defaultValues?.forAnchorPoints
        ? "only-anchor-point"
        : defaultValues?.forLifeLines
          ? "only-life-line"
          : "both";

  return (
    <Form<SupportExtraFormBody>
      defaultValues={{
        ...defaultValues,
        factor: defaultValues?.factor ?? 1,
        type: type,
      }}
      submitText={i18n.translation.common.save}
      disabled={isSubmitting}
      secondaryButton={
        <Button buttonProps={{ onClick: () => onClose() }} type="secondary">
          {i18n.translation.common.cancel}
        </Button>
      }
      onSubmit={() => async (formData) => {
        const update: UpdateSupportExtraBody = {
          systemId: patrolSystemId ?? formData.system?.id!,
          roofSubstructureId: formData.roofSubstructure?.id,
          seamHeight: formData.seamHeight ?? undefined,
          minThickness: formData.minThickness ?? undefined,
          maxThickness: formData.maxThickness ?? undefined,
          forLifeLines: formData.type === "only-life-line" || formData.type === "both",
          forAnchorPoints: formData.type === "only-anchor-point" || formData.type === "both",
          factor: formData.factor ?? 1,
        };

        if (defaultValues?.id) {
          try {
            await supportExtrasApi.updateAdminSupportExtra(defaultValues.id, update);
            onClose();
          } catch (error) {
            dispatch(showErrorNotification(i18n, error));
          } finally {
            setIsSubmitting(false);
          }
        } else {
          const insert: InsertSupportExtraBody = {
            ...update,
            itemId: formData.item?.id!,
          };

          try {
            await supportExtrasApi.insertAdminSupportExtra(insert);
            onClose();
          } catch (error) {
            dispatch(showErrorNotification(i18n, error));
          } finally {
            setIsSubmitting(false);
          }
        }
      }}
    >
      {({ control, setValue }) => {
        return (
          <FormFieldsContainer fullWidth>
            <Controller
              name="item"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => {
                return (
                  <>
                    <FormField label={i18n.translation.items.singular} error={fieldState.error}>
                      {({ labelId }) => {
                        const searchTerm = useRef("");
                        const aborter = useRef(new AbortController());

                        const loadOptions = createLoadOptions(aborter, searchTerm, itemsApi.getAdminItems);

                        // Fetch initial data on mount
                        useEffect(() => {
                          loadOptions(searchTerm.current).then(setDefaultOptions);
                        }, []);

                        return (
                          <CustomAsyncSelect
                            {...field}
                            inputId={labelId}
                            defaultOptions={defaultOptions}
                            loadOptions={loadOptions}
                            isClearable
                            isDisabled={isSubmitting || !!defaultValues?.id}
                            getOptionLabel={(x) => x.code}
                            getOptionValue={(x) => x.id}
                            onChange={(value) => setValue("item", value ?? null)}
                          />
                        );
                      }}
                    </FormField>
                  </>
                );
              }}
            />
            <Controller
              name="system"
              control={control}
              rules={{ required: !patrolSystemId }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.patrolSystems.singular}
                  error={fieldState.error}
                  hidden={!!patrolSystemId}
                >
                  {({ labelId }) => (
                    <CustomSelect<AdminPatrolSystem>
                      {...field}
                      inputId={labelId}
                      options={patrolSystemsResult.data?.items ?? []}
                      isDisabled={!!patrolSystemsResult.error || isSubmitting}
                      isLoading={patrolSystemsResult.isLoading}
                      getOptionValue={(x) => x.id}
                      getOptionLabel={(x) => x.name}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="type"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.type} error={fieldState.error}>
                  {({ labelId }) => (
                    <CustomSelect<extraType>
                      {...field}
                      inputId={labelId}
                      options={extraTypes}
                      isDisabled={isSubmitting}
                      isLoading={isSubmitting}
                      getOptionValue={(x) => x.value}
                      getOptionLabel={(x) => x.title}
                      value={extraTypes.find((option) => option.value === field.value) ?? null}
                      onChange={(option) => setValue("type", extraTypes.find((s) => s.value === option?.value)?.value!)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="factor"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.factor} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      disabled={isSubmitting}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      onBlur={(value) => setValue("factor", value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="roofSubstructure"
              control={control}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.roofSubstructures.singular} error={fieldState.error} isOptional>
                  {({ labelId }) => (
                    <CustomSelect<AdminRoofSubstructure>
                      {...field}
                      inputId={labelId}
                      options={roofSubstructureResult.data?.items ?? []}
                      isDisabled={!!roofSubstructureResult.error || isSubmitting}
                      isLoading={roofSubstructureResult.isLoading}
                      getOptionValue={(x) => x.id}
                      getOptionLabel={(x) => x.name}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="seamHeight"
              control={control}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.seamHeight} error={fieldState.error} isOptional>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      disabled={isSubmitting}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      onBlur={(value) => setValue("seamHeight", value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="minThickness"
              control={control}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.minThickness} error={fieldState.error} isOptional>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      disabled={isSubmitting}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      onBlur={(value) => setValue("minThickness", value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="maxThickness"
              control={control}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.maxThickness} error={fieldState.error} isOptional>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      disabled={isSubmitting}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      onBlur={(value) => setValue("maxThickness", value)}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default SupportExtraForm;
